import { uiSurface } from "shared/ui/uiSurface"
import { Description, OpenInNew, Store } from "@mui/icons-material"
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material"
import dayjs from "dayjs"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { OrderDetail } from "src/db/OrderDetail"
import { appConstants } from "src/features/shared-module/constants"
import {
  formattedCurrency,
  getFullName,
  getMapLinkUrlByAddress,
  getOneLineAddress,
} from "src/features/shared-module/functions"
import { OrderTagLabel } from "./OrderTagLabel"
import { SkeletonWrapper } from "src/features/shared-module/components"

export const OrderDetailView: FC<{
  orderDetail?: OrderDetail
  isLoading: boolean
}> = ({ orderDetail, isLoading }) => {
  const { t } = useTranslation()

  if (isLoading) {
    return <SkeletonOrderDetailView />
  }

  if (!orderDetail) {
    return <Box>No Details found.</Box>
  }

  const allTagList = [
    ...orderDetail.client.tags.map((e) => e.tag),
    ...orderDetail.tags.map((e) => e.tag),
  ]

  const oneLineAddress = getOneLineAddress(orderDetail.address)

  return (
    <Box {...uiSurface("UI-DSP-025")}>
      <Stack
        direction={"row"}
        alignItems={"flex-end"}
        justifyContent={"space-between"}
      >
        <Box>
          <Typography data-testid="order-detail-client-name" color="primary" variant="h1">
            {getFullName(
              orderDetail.client.firstName,
              orderDetail.client.lastName,
            )}
          </Typography>
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <Store color="secondary" />
            <Typography data-testid="order-detail-hauler" color="secondary" variant="h3">
              {orderDetail.haulerEmployee?.hauler?.name ?? "Unassigned"}
            </Typography>
          </Stack>
          {orderDetail?.recurringOrder?.averageDeliveryMoment && (
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={0.5}
              lineHeight={1}
            >
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={(theme) => ({
                  span: {
                    padding: 0.5,
                  },
                })}
              >
                {t("usualArrivalHour")}:
              </Typography>
              <Chip
                label={dayjs(
                  orderDetail.recurringOrder.averageDeliveryMoment,
                ).format(appConstants.timeFormat_hhmmA)}
                sx={(theme) => ({
                  backgroundColor: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                  borderRadius: 2,
                  marginLeft: 0.5,
                  fontWeight: 500,
                })}
              />
            </Stack>
          )}
        </Box>
        <Typography data-testid="order-detail-amount" variant="h3">
          {formattedCurrency(orderDetail.amount || 0)}
        </Typography>
      </Stack>
      <Divider
        sx={{
          marginY: 1,
        }}
      />
      <Stack direction={"row"} alignItems={"center"}>
        <Typography data-testid="order-detail-address" variant="body1" color="primary">
          {oneLineAddress}
        </Typography>
        <IconButton
          data-testid="order-detail-map-link"
          onClick={() => {
            window.open(getMapLinkUrlByAddress(oneLineAddress), "_blank")
          }}
          color="primary"
          size="small"
        >
          <OpenInNew />
        </IconButton>
      </Stack>
      {orderDetail.address.notes && (
        <>
          <NoteView
            noteLabel={t("addressNote")}
            note={orderDetail.address.notes}
          />
          <Divider
            sx={{
              marginY: 2,
            }}
          />
        </>
      )}
      {orderDetail.client.notes && (
        <>
          <NoteView
            noteLabel={t("clientNote")}
            note={orderDetail.client.notes}
          />
          <Divider
            sx={{
              marginY: 2,
            }}
          />
        </>
      )}
      {orderDetail.notes && (
        <>
          <NoteView noteLabel={t("orderNote")} note={orderDetail.notes} />
          <Divider
            sx={{
              marginY: 2,
            }}
          />
        </>
      )}
      {orderDetail?.driverOwnedClientDetail?.notes && (
        <>
          <NoteView
            noteLabel={t("courierNote")}
            note={orderDetail.driverOwnedClientDetail.notes}
          />
          <Divider
            sx={{
              marginY: 2,
            }}
          />
        </>
      )}
      <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
        {allTagList.map((tag, index) => {
          return (
            <Chip
              key={`tag-${index}`}
              label={<OrderTagLabel tag={tag} />}
              sx={(theme) => ({
                backgroundColor: tag.color,
                paddingX: 1,
                borderRadius: 1,
                color: theme.palette.common.white,
                "& .MuiChip-icon": {
                  color: theme.palette.common.white,
                  margin: 0,
                },
              })}
              icon={
                <Box
                  sx={() => ({
                    fontSize: 16,
                  })}
                  component={"i"}
                  className={`fa fa-${tag.icon}`}
                />
              }
            />
          )
        })}
      </Stack>
    </Box>
  )
}

const NoteView: FC<{ noteLabel: string; note: string }> = ({
  noteLabel,
  note,
}) => {
  return (
    <Stack direction={"row"} alignItems={"center"}>
      <Stack direction={"row"} alignItems={"center"} marginRight={1}>
        <IconButton
          size="small"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
          })}
        >
          <Description />
        </IconButton>
        <Typography
          variant="subtitle2"
          color="textSecondary"
          noWrap
          width={100}
        >
          {noteLabel}:
        </Typography>
      </Stack>
      <Typography variant="body1" color="textPrimary">
        {note}
      </Typography>
    </Stack>
  )
}

const SkeletonOrderDetailView = () => {
  return (
    <Box>
      <Stack
        direction={"row"}
        alignItems={"flex-end"}
        justifyContent={"space-between"}
      >
        <Stack spacing={1}>
          <SkeletonWrapper width={120} />
          <SkeletonWrapper width={200} />
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={0.5}
            lineHeight={1}
          >
            <SkeletonWrapper width={125} height={22} />
            <SkeletonWrapper
              sxProps={{
                borderRadius: 2,
              }}
              width={90}
              height={34}
            />
          </Stack>
        </Stack>
        <SkeletonWrapper width={60} height={18} />
      </Stack>
      <Divider
        sx={{
          marginY: 1,
        }}
      />
      <SkeletonWrapper width={280} height={30} />
      <Divider
        sx={{
          marginY: 1,
        }}
      />
      {Array.from({ length: 4 }, (e, i) => i).map((e: number) => {
        return (
          <>
            <SkeletonWrapper
              key={e}
              height={50}
              sxProps={{
                borderRadius: 1,
              }}
            />
            <Divider
              sx={{
                marginY: 2,
              }}
            />
          </>
        )
      })}
      <Stack direction={"row"} flexWrap={"wrap"} gap={1}>
        {Array.from({ length: 4 }, (e, i) => i).map((e: number) => {
          return (
            <>
              <SkeletonWrapper
                key={e}
                height={32}
                width={140}
                sxProps={{
                  borderRadius: 2,
                }}
              />
              <Divider
                sx={{
                  marginY: 2,
                }}
              />
            </>
          )
        })}
      </Stack>
    </Box>
  )
}
